import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import axios from 'axios';

import {
    MDBContainer,
    MDBRow,
    MDBCol,
  } from 'mdb-react-ui-kit';

import Fab from '@mui/material/Fab';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CallEndIcon from '@mui/icons-material/CallEnd';

const Room = () => {
    const location = useLocation();
    const userVideo = useRef();
    const userStream = useRef();
    const remoteVideoRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const peerRef = useRef();
    const webSocketRef = useRef();
    const [triggeredAlready, setTriggered] = useState(false)
    const isConnected = useRef()
    const [showControls, setShowControls] = useState(false)
    const [isMicMuted, setMicMuted] = useState(true)

    // Generate a unique ID for this session to identify this guest
    const userID = useRef(Math.floor(Math.random() * 10000000).toString());
    
    const navigate = useNavigate()

    const checkConnection = () => {
        return showControls
    }

    const handleTrack = (event) => {
        const track = event.track;
        if (track.kind === 'video') {
            if (event.transceiver.mid == 0) {
                remoteVideoRef.current.srcObject = new MediaStream([track]);
            } else {
                 // Fallback if mid is different
                 remoteVideoRef.current.srcObject = new MediaStream([track]);
            }
        } else if (track.kind === 'audio') {
            remoteAudioRef.current.srcObject = new MediaStream([track]);
        }
    };

  const sendTURNCredRequest = async (email) => {
    const apiURL = "https://topmod-backend.daqa.net/api/v1/collab/getAuth"
    let creds = {};

    try {
      const payload = { email: email };
      const response = await axios.post(apiURL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
  
      if (response.status === 200) {
        creds.username = response.data.username;
        creds.password = response.data.password;
        return creds;
      } else {
        throw new Error(`Invalid Request With Status: ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  }
    
    const openCamera = async () => {
        const constraints = { video: false, audio: true };
        await navigator.mediaDevices.getUserMedia(constraints).then((stream) =>{
            if (userVideo.current != null) {
                userVideo.current.srcObject = stream
                userStream.current = stream
            }
        })
    };

    const MessageHandler = async (e) => {
        const message = JSON.parse(e.data);
        
        if (message.error != undefined) {
            webSocketRef.current = null
            if (location.pathname != "/room/test") {
                navigate("/", { state: { error: "Room does not exist." } })
            }
        }

        // Host sends offers with a 'target' property.
        // We must ignore offers meant for other guests.
        if (message.target && message.target !== userID.current) {
            return;
        }

        if (isConnected.current === true) {
            
            // MODIFICATION: Only disconnect if the message comes from the Host
            if (message.type == "disconnect") {
                if (message.sender === "TopModQA") {
                     disconnectCall()
                }
            }

            if (message.type == "offer" && message.message.length > 0) {
                    handleOffer(message.message);
            }
        }

        if (message.type == "candidate") {
            try {
                if (peerRef.current) {
                    await peerRef.current.addIceCandidate(message.message);
                }
            } catch(err) {
                console.log("error ICE CANDIDADE")
            }
        }
    }

    useEffect(() => {
        if (location.pathname != "/room/test") {
            if (!triggeredAlready) {
                setTriggered(true)
                isConnected.current = false
                openCamera().then( async () => {
                    const roomID = location.pathname.split("/");
                    webSocketRef.current = await new WebSocket(`wss://turn.daqa.net:21338/?host=false&room=${roomID[2]}`)

                    webSocketRef.current.onopen = async (event) => {
                        console.log("Connecting...")
                        if (webSocketRef.current.readyState == 1) {
                            console.log("Ready!")
                            isConnected.current = true
                            setShowControls(true)
                            createPeer().then(async (peer) => {
                                peerRef.current = peer
                                // Send "join" with our unique sender ID
                                if (webSocketRef.current != null) {
                                    await webSocketRef.current.send(
                                        JSON.stringify({ sender: userID.current, type: "join", message: "Init" })
                                    )
                                }
                            });
                        }
                    };

                    await webSocketRef.current.addEventListener("message", MessageHandler)
                    setTimeout(() => {
                        muteMic()
                    }, 2500)
                })
            }
        }
    }, []);

    const handleOffer = async (offer) => {
        console.log("Received Offer, Creating Answer");
        
        if (!peerRef.current) {
             peerRef.current = await createPeer();
        }
        
        await peerRef.current.setRemoteDescription(
            new RTCSessionDescription({type: "offer", sdp: offer})
        );

        // Add tracks SAFELY (ignoring if they already exist)
        if (userStream.current) {
            userStream.current.getTracks().forEach((track) => {
                try {
                    peerRef.current.addTrack(track, userStream.current);
                } catch (err) {
                    // Ignore "Sender already exists" error
                }
            });
        }

        const answer = await peerRef.current.createAnswer();
        await peerRef.current.setLocalDescription(answer);

        await webSocketRef.current.send(
            JSON.stringify({ 
                sender: userID.current, 
                type: "answer", 
                message: peerRef.current.localDescription.sdp 
            })
        );
    };

    const createPeer = () => {
        return sendTURNCredRequest("client").then((creds)=> {
            const peer = new RTCPeerConnection({
                iceServers: [{ 
                    urls: "turn:turn.daqa.net:5349?transport=udp",
                    username: creds.username,
                    credential: creds.password
                 }],
                 iceTransportPolicy: "relay",
            });
    
            // Prevent infinite loop by disabling negotiation needed on the Guest side
            peer.onnegotiationneeded = () => {}; 
            
            peer.addTransceiver("video")
            peer.onicecandidate = handleIceCandidateEvent;
            peer.ontrack = handleTrack;
            return peer;
        })
    };

    const handleIceCandidateEvent = async (e) => {
        if (e.candidate) {
            await webSocketRef.current.send(
                JSON.stringify({ sender: userID.current, type: "candidate", message: e.candidate })
            );
        }
    };

    const disconnectCall = async () => {
        await webSocketRef.current.send(
            JSON.stringify({ sender: userID.current, type: "disconnect" })
        );
        webSocketRef.current.removeEventListener("message", MessageHandler)
        webSocketRef.current = null
        peerRef.current = null
        if (userVideo.current && userVideo.current.srcObject) {
            userVideo.current.srcObject.getTracks().forEach(track => track.stop());
        }
        userVideo.current = null
        userStream.current = null
        navigate("/", { state: { error: "Connection Closed." } })
    }

    const muteMic = () => {
        if (userVideo.current && userVideo.current.srcObject) {
            const audioTracks = userVideo.current.srcObject.getAudioTracks();
            if (audioTracks.length > 0) {
                const enabled = !audioTracks[0].enabled;
                setMicMuted(!enabled);
                audioTracks[0].enabled = enabled;
            }
        }
    }

  return (
    <MDBContainer className="gradient-form">
        <video className='tester-vid' ref={remoteVideoRef} autoPlay playsInline />
        <MDBRow>
            <MDBCol md='4' className="col-md-4 mb-5"></MDBCol>
            <MDBCol md='4' className="col-md-4 mb-5">
                <div className="d-flex flex-column">
                    <div className="logo mt-12 transparent"></div>                   
                    <audio className='hidden' playsInline autoPlay muted controls={true} ref={userVideo} />
                    <audio className='hidden' ref={remoteAudioRef} autoPlay playsInline />
                </div>
            </MDBCol>
            { checkConnection() ?
                <MDBCol md='4' className="col-md-4 mb-5">
                    <div className='control-bar'>
                        <Fab disabled={true} color="primary" aria-label="add" onClick={() => {muteMic()}}>
                            { isMicMuted ? <MicIcon /> : <MicOffIcon /> }
                        </Fab>
                        <Fab color="secondary" aria-label="edit" onClick={() => { disconnectCall()}}>
                            <CallEndIcon />
                        </Fab>
                    </div>
                </MDBCol>
            : null }
        </MDBRow>
    </MDBContainer>
  )
}

export default Room;